import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ClipboardPlusIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DocumentList } from "@/components/app/attachments";
import { ChassisCodeList } from "@/components/app/chassis-codes";
import { LinksPanel } from "@/components/app/links";
import { Markdown } from "@/components/app/markdown";
import { Page, PageHeader, SectionTitle } from "@/components/app/page";
import { PartPicker } from "@/components/app/part-picker";
import { WorkOrderStatusBadge } from "@/components/app/status";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/misc";
import { PartSheet } from "@/features/parts/part-sheet";
import { ApplyTemplateDialog } from "@/features/templates/apply-template-dialog";
import { ServiceIntervalLabel } from "@/features/templates/service-interval";
import { TemplateSheet } from "@/features/templates/template-sheet";
import { RequirementList } from "@/features/work-orders/requirement-list";
import { useFormat } from "@/lib/format";
import { usePriceBasis } from "@/lib/prices";
import { useTRPC } from "@/lib/trpc";

export const Route = createFileRoute("/templates/$templateId")({
  component: TemplatePage,
});

function TemplatePage() {
  const { templateId } = Route.useParams();
  const { t } = useTranslation();
  const trpc = useTRPC();
  const format = useFormat();
  const priceBasis = usePriceBasis();
  const navigate = useNavigate();
  const { data: tpl, isLoading } = useQuery(trpc.templates.get.queryOptions({ id: templateId }));
  const [editing, setEditing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [creatingPart, setCreatingPart] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const addPart = useMutation(trpc.templates.addPart.mutationOptions());
  const updatePart = useMutation(trpc.templates.updatePart.mutationOptions());
  const removePart = useMutation(trpc.templates.removePart.mutationOptions());
  const remove = useMutation(trpc.templates.delete.mutationOptions());

  if (isLoading || !tpl) {
    return (
      <Page>
        <Skeleton className="h-64 rounded-xl" />
      </Page>
    );
  }

  const add = (partId: string) =>
    addPart.mutate({
      templateId: tpl.id,
      data: { partId, qty: 1000, consumesStock: true, note: null },
    });

  return (
    <Page>
      <PageHeader
        crumbs={[{ label: t("nav.templates"), to: "/templates" }]}
        title={tpl.title}
        subtitle={
          <span className="flex min-w-0 flex-wrap items-center gap-2">
            <Badge tone="outline">{t(`workOrderType.${tpl.type}`)}</Badge>
            <ServiceIntervalLabel interval={tpl.interval} />
            <ChassisCodeList codes={tpl.chassisCodes} max={8} />
          </span>
        }
        actions={
          <>
            <Button variant="outline" onClick={() => setEditing(true)}>
              <PencilIcon />
              {t("common.edit")}
            </Button>
            <Button onClick={() => setApplying(true)}>
              <ClipboardPlusIcon />
              {t("templates.createWorkOrder")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={t("common.more")}>
                  <MoreHorizontalIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem destructive onSelect={() => setConfirmDelete(true)}>
                  <Trash2Icon />
                  {t("common.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />
      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        <div className="flex min-w-0 flex-col gap-7">
          <Card className="overflow-hidden">
            <CardHeader className="items-start pb-3">
              <div className="min-w-0">
                <CardTitle>{t("workOrders.parts")}</CardTitle>
                <p className="mt-0.5 text-sm text-muted-foreground">{t("templates.partsHint")}</p>
                {tpl.estimate.priced || tpl.estimate.unpriced ? (
                  <p className="mt-1.5 text-sm">
                    {tpl.estimate.priced ? (
                      <>
                        <span className="text-muted-foreground">{t("prices.estimate")}</span>{" "}
                        <span className="font-semibold tabular">
                          {format.moneyRange(tpl.estimate)}
                        </span>{" "}
                        <span className="text-xs text-muted-foreground">{priceBasis}</span>
                      </>
                    ) : null}
                    {tpl.estimate.unpriced ? (
                      <span className="block text-xs text-muted-foreground">
                        {t("prices.unpriced", { count: tpl.estimate.unpriced })}
                      </span>
                    ) : null}
                  </p>
                ) : null}
              </div>
              <PartPicker
                onSelect={(part) => add(part.id)}
                onCreate={(name) => setCreatingPart(name)}
                trigger={
                  <Button variant="outline" size="sm">
                    <PlusIcon />
                    {t("workOrders.addPart")}
                  </Button>
                }
              />
            </CardHeader>
            {tpl.parts.length ? (
              <div className="border-t border-border">
                <RequirementList
                  lines={tpl.parts}
                  readOnly={false}
                  qtyLabel={t("templates.qtyColumn")}
                  onUpdate={(id, data) => updatePart.mutate({ id, data })}
                  onRemove={(id) => removePart.mutate({ id })}
                />
              </div>
            ) : (
              <CardContent>
                <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
                  {t("templates.partsEmpty")}
                </p>
              </CardContent>
            )}
          </Card>
          {tpl.description ? (
            <section>
              <SectionTitle>{t("common.description")}</SectionTitle>
              <Card className="p-5">
                <Markdown>{tpl.description}</Markdown>
              </Card>
            </section>
          ) : null}
          <div className="grid gap-7 md:grid-cols-2">
            <section>
              <SectionTitle>{t("common.links")}</SectionTitle>
              <LinksPanel ownerType="template" ownerId={tpl.id} links={tpl.links} />
            </section>
            <section>
              <SectionTitle>{t("common.documents")}</SectionTitle>
              <DocumentList ownerType="template" ownerId={tpl.id} attachments={tpl.attachments} />
            </section>
          </div>
        </div>
        <aside>
          <Card>
            <CardHeader>
              <CardTitle>{t("templates.workOrders")}</CardTitle>
            </CardHeader>
            {tpl.workOrders.length ? (
              <div className="divide-y divide-border border-t border-border">
                {tpl.workOrders.map((wo) => (
                  <Link
                    key={wo.id}
                    to="/work-orders/$workOrderId"
                    params={{ workOrderId: wo.id }}
                    className="flex items-center justify-between gap-2 px-4 py-2.5 text-sm transition hover:bg-muted/50"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium">{wo.vehicleName}</div>
                      <div className="text-xs text-muted-foreground">
                        {wo.completedOn ? format.date(wo.completedOn) : wo.title}
                      </div>
                    </div>
                    <WorkOrderStatusBadge status={wo.status} />
                  </Link>
                ))}
              </div>
            ) : (
              <CardContent className="text-sm text-muted-foreground">
                {t("templates.neverUsed")}
              </CardContent>
            )}
          </Card>
        </aside>
      </div>
      <TemplateSheet open={editing} onOpenChange={setEditing} template={tpl} />
      <ApplyTemplateDialog open={applying} onOpenChange={setApplying} templateId={tpl.id} />
      <PartSheet
        open={creatingPart !== null}
        onOpenChange={(open) => !open && setCreatingPart(null)}
        initialName={creatingPart ?? undefined}
        onSaved={(id) => add(id)}
      />
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t("common.deleteConfirmTitle", { name: tpl.title })}
        confirmLabel={t("common.delete")}
        destructive
        onConfirm={() =>
          remove.mutate(
            { id: tpl.id },
            {
              onSuccess: () => {
                toast.success(t("templates.deleted"));
                void navigate({ to: "/templates" });
              },
            },
          )
        }
      />
    </Page>
  );
}
